import { useState, useEffect, useMemo, useRef } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { Save, RotateCcw, Loader2, Plus, Trash2, X, FileUp, ClipboardPaste } from 'lucide-react'
import { useCaptionPresets, useSaveCaptionPresets } from '@/api/hooks/useCaptionPresets'
import { captionPresets as defaultPresets } from './captionPresets'
import { useLanguage } from '@/i18n/LanguageContext'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const DEFAULT_TONES = ['seducao', 'casual', 'religioso', 'motivacional']
const DEFAULT_LABELS: Record<string, string> = {
  seducao: 'Seducao',
  casual: 'Casual',
  religioso: 'Religioso',
  motivacional: 'Motivacional',
}

export function CaptionPresetEditor({ open, onOpenChange }: Props) {
  const { t } = useLanguage()
  const { data: serverPresets } = useCaptionPresets()
  const saveMutation = useSaveCaptionPresets()
  const [activeTone, setActiveTone] = useState('casual')
  const [texts, setTexts] = useState<Record<string, string>>({})
  const [showNewTone, setShowNewTone] = useState(false)
  const [newToneName, setNewToneName] = useState('')
  const [showBulkPaste, setShowBulkPaste] = useState(false)
  const [bulkText, setBulkText] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Build list of all tones (defaults + custom)
  const allTones = useMemo(() => {
    const tones: Array<{ value: string; label: string; isCustom: boolean; count: number }> = []
    // Defaults first
    for (const key of DEFAULT_TONES) {
      const count = texts[key]?.split('\n').filter(s => s.trim()).length || 0
      tones.push({ value: key, label: DEFAULT_LABELS[key], isCustom: false, count })
    }
    // Custom tones from texts state
    for (const key of Object.keys(texts)) {
      if (!DEFAULT_TONES.includes(key)) {
        const count = texts[key]?.split('\n').filter(s => s.trim()).length || 0
        tones.push({ value: key, label: key, isCustom: true, count })
      }
    }
    return tones
  }, [texts])

  useEffect(() => {
    if (open) {
      const source = serverPresets || defaultPresets
      const parsed: Record<string, string> = {}
      for (const key of Object.keys(source)) {
        parsed[key] = (source[key] || []).join('\n')
      }
      // Ensure defaults exist
      for (const key of DEFAULT_TONES) {
        if (!parsed[key]) {
          parsed[key] = (defaultPresets[key] || []).join('\n')
        }
      }
      setTexts(parsed)
      setActiveTone('casual')
      setShowNewTone(false)
      setNewToneName('')
      setShowBulkPaste(false)
      setBulkText('')
    }
  }, [open, serverPresets])

  const handleSave = () => {
    const presets: Record<string, string[]> = {}
    for (const [tone, text] of Object.entries(texts)) {
      const lines = text.split('\n').map(s => s.trim()).filter(s => s.length > 0)
      if (lines.length > 0) {
        presets[tone] = lines
      }
    }
    saveMutation.mutate(presets, {
      onSuccess: () => {
        toast.success(t('Templates salvos!'))
        onOpenChange(false)
      },
      onError: () => toast.error(t('Erro ao salvar')),
    })
  }

  const handleResetTone = () => {
    if (DEFAULT_TONES.includes(activeTone)) {
      const defaults = defaultPresets[activeTone] || []
      setTexts(prev => ({ ...prev, [activeTone]: defaults.join('\n') }))
      toast.success(t('Restaurado para padrao'))
    }
  }

  const handleAddTone = () => {
    const name = newToneName.trim().toLowerCase().replace(/\s+/g, '_')
    if (!name) return
    if (texts[name] !== undefined) {
      toast.error(t('Categoria ja existe'))
      return
    }
    setTexts(prev => ({ ...prev, [name]: '' }))
    setActiveTone(name)
    setShowNewTone(false)
    setNewToneName('')
    toast.success(t('Categoria criada'))
  }

  const handleDeleteTone = (tone: string) => {
    if (DEFAULT_TONES.includes(tone)) return
    if (!confirm(`Deletar categoria "${tone}" e todas suas legendas?`)) return
    setTexts(prev => {
      const next = { ...prev }
      delete next[tone]
      return next
    })
    if (activeTone === tone) setActiveTone('casual')
    toast.success(t('Categoria removida'))
  }

  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (evt) => {
      const content = evt.target?.result as string
      if (!content?.trim()) {
        toast.error(t('Arquivo vazio'))
        return
      }
      const newLines = content.split('\n').map(s => s.trim()).filter(s => s.length > 0)
      const current = texts[activeTone] || ''
      const currentLines = current.split('\n').filter(s => s.trim())
      const merged = [...currentLines, ...newLines]
      setTexts(prev => ({ ...prev, [activeTone]: merged.join('\n') }))
      toast.success(`${newLines.length} ${t('frases importadas!')}`)
    }
    reader.readAsText(file)
    // Reset input so same file can be selected again
    e.target.value = ''
  }

  const handleBulkPaste = () => {
    if (!bulkText.trim()) {
      toast.error(t('Nenhuma frase para importar'))
      return
    }
    const newLines = bulkText.split('\n').map(s => s.trim()).filter(s => s.length > 0)
    const current = texts[activeTone] || ''
    const currentLines = current.split('\n').filter(s => s.trim())
    const merged = [...currentLines, ...newLines]
    setTexts(prev => ({ ...prev, [activeTone]: merged.join('\n') }))
    toast.success(`${newLines.length} ${t('frases adicionadas!')}`)
    setShowBulkPaste(false)
    setBulkText('')
  }

  const currentLines = texts[activeTone]?.split('\n').filter(s => s.trim()).length || 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-4 sm:p-6">
        <DialogHeader className="shrink-0">
          <DialogTitle className="text-sm sm:text-base">
            {t('Gerenciar Categorias e Frases')}
          </DialogTitle>
          <p className="text-[11px] text-muted-foreground mt-1">
            {t('Crie categorias ilimitadas e adicione quantas frases quiser. Uma frase por linha.')}
          </p>
        </DialogHeader>

        <div className="space-y-3 overflow-y-auto min-h-0 flex-1">
          {/* Tone tabs with counts */}
          <div className="space-y-1.5">
            <div className="flex flex-wrap gap-1.5">
              {allTones.map(tone => (
                <button
                  key={tone.value}
                  type="button"
                  onClick={() => setActiveTone(tone.value)}
                  className={cn(
                    'relative flex items-center gap-1 rounded-md px-2.5 py-1.5 text-[11px] sm:text-xs font-medium transition-colors',
                    activeTone === tone.value
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:text-foreground',
                  )}
                >
                  {tone.label}
                  <span className={cn(
                    'inline-flex items-center justify-center rounded-full px-1.5 min-w-[18px] h-4 text-[10px] font-bold',
                    activeTone === tone.value
                      ? 'bg-primary-foreground/20 text-primary-foreground'
                      : 'bg-background text-muted-foreground',
                  )}>
                    {tone.count}
                  </span>
                  {tone.isCustom && activeTone === tone.value && (
                    <button
                      type="button"
                      className="ml-0.5 inline-flex items-center hover:text-destructive"
                      onClick={(e) => { e.stopPropagation(); handleDeleteTone(tone.value) }}
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  )}
                </button>
              ))}
              {/* Add new tone button */}
              {showNewTone ? (
                <div className="flex items-center gap-1">
                  <Input
                    value={newToneName}
                    onChange={(e) => setNewToneName(e.target.value)}
                    placeholder={t('Nome da categoria...')}
                    className="h-7 w-36 text-[11px] px-2"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleAddTone()
                      if (e.key === 'Escape') { setShowNewTone(false); setNewToneName('') }
                    }}
                  />
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={handleAddTone}>
                    <Plus className="h-3 w-3 text-primary" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setShowNewTone(false); setNewToneName('') }}>
                    <X className="h-3 w-3 text-muted-foreground" />
                  </Button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowNewTone(true)}
                  className="flex items-center gap-1 rounded-md border border-dashed border-primary/40 bg-primary/5 px-3 py-1.5 text-[11px] sm:text-xs font-medium text-primary hover:bg-primary/10 transition-colors"
                >
                  <Plus className="h-3 w-3" /> {t('Nova Categoria')}
                </button>
              )}
            </div>
          </div>

          {/* Bulk import area */}
          {showBulkPaste ? (
            <div className="space-y-2 rounded-lg border border-border bg-muted/30 p-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium">{t('Colar frases em massa')}</p>
                <button
                  type="button"
                  onClick={() => { setShowBulkPaste(false); setBulkText('') }}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              <Textarea
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                placeholder={t('Cole aqui suas frases (uma por linha)...\n\nExemplo:\nFrase 1 aqui\nFrase 2 aqui\nFrase 3 aqui')}
                className="min-h-[120px] text-xs leading-relaxed resize-none"
                autoFocus
              />
              <div className="flex items-center justify-between">
                <p className="text-[11px] text-muted-foreground">
                  {bulkText.split('\n').filter(s => s.trim()).length} {t('frases para adicionar')}
                </p>
                <Button size="sm" className="gap-1.5 text-xs" onClick={handleBulkPaste}>
                  <Plus className="h-3 w-3" /> {t('Adicionar a')} "{activeTone}"
                </Button>
              </div>
            </div>
          ) : (
            /* Import buttons */
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".txt,.csv"
                className="hidden"
                onChange={handleImportFile}
              />
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 text-xs"
                onClick={() => fileInputRef.current?.click()}
              >
                <FileUp className="h-3.5 w-3.5" /> {t('Importar .txt')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 text-xs"
                onClick={() => setShowBulkPaste(true)}
              >
                <ClipboardPaste className="h-3.5 w-3.5" /> {t('Colar em Massa')}
              </Button>
            </div>
          )}

          {/* Textarea */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-[11px] text-muted-foreground">
                {t('Uma legenda por linha')} ({currentLines} {t('legendas')})
              </p>
              {DEFAULT_TONES.includes(activeTone) && (
                <button
                  type="button"
                  onClick={handleResetTone}
                  className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
                >
                  <RotateCcw className="h-3 w-3" /> {t('Restaurar padrao')}
                </button>
              )}
            </div>
            <Textarea
              value={texts[activeTone] || ''}
              onChange={(e) => setTexts(prev => ({ ...prev, [activeTone]: e.target.value }))}
              placeholder={t('Uma legenda por linha...')}
              className="min-h-[250px] text-xs leading-relaxed resize-none"
            />
          </div>

        </div>

        {/* Actions - fixed at bottom */}
        <div className="flex items-center justify-end gap-2 shrink-0 pt-2 border-t border-border">
          <Button size="sm" variant="ghost" className="text-xs" onClick={() => onOpenChange(false)}>
            {t('Cancelar')}
          </Button>
          <Button
            size="sm"
            className="gap-1.5 text-xs"
            onClick={handleSave}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            {t('Salvar')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
